/** The zone every business date is computed in.
 *
 *  Its own screen rather than a row in the Settings table, for the reason the
 *  Message channel has one: the save is not the whole story. The zone is read once
 *  when the container starts, so a saved value that differs from the running one is a
 *  pending restart, and a table of raw key/value boxes has nowhere to say that.
 *
 *  Superuser only, and the API enforces it - `PATCH /settings` refuses the key
 *  outright and points here, so this page is the only way in rather than the
 *  convenient way in.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { Banner, ErrorNote, Spinner } from '@/components/ui'

type Status = {
  timezone: string
  running: string
  restart_required: boolean
  common: string[]
  zones: string[]
}

export function Timezone() {
  const qc = useQueryClient()
  const status = useQuery({
    queryKey: ['timezone'],
    queryFn: () => api.get<Status>('/timezone'),
  })
  const [choice, setChoice] = useState<string | null>(null)
  const [err, setErr] = useState<unknown>(null)

  const save = useMutation({
    mutationFn: (timezone: string) => api.put('/timezone', { timezone }),
    onSuccess: () => {
      setErr(null); setChoice(null)
      qc.invalidateQueries({ queryKey: ['timezone'] })
      qc.invalidateQueries({ queryKey: ['settings'] })
    },
    onError: setErr,
  })

  if (status.isLoading) return <Spinner label="Loading the timezone" />
  if (status.error) return <ErrorNote error={status.error} />
  const s = status.data!
  const want = choice ?? s.timezone
  const dirty = want !== s.timezone

  return (
    <>
      <h2 className="text-[17px] font-semibold tracking-tight mb-1">Timezone</h2>
      <p className="text-[12.5px] text-ink-3 mb-4 max-w-2xl leading-relaxed">
        Every date in the app is a date in this zone — which day a task belongs to,
        which day gets closed, and what the times on the Reminders and Day close
        screens mean. Timestamps are stored in UTC and shown in this zone, so
        changing it re-reads history rather than rewriting it.
      </p>

      {err ? <div className="mb-4"><ErrorNote error={err} /></div> : null}

      {/* The pending-restart state, said plainly and before the control. Somebody who
          saved and walked away needs to know the app is still on the old zone; the
          screen cannot infer it, so the server reports it. */}
      {s.restart_required && (
        <div className="mb-4">
          <Banner tone="warn">
            <b>Saved, and not applied yet.</b> The app is set to{' '}
            <b>{s.timezone}</b> and still running <b>{s.running}</b>. Restart the api
            container to apply it:{' '}
            <code className="mono text-[11.5px]">
              docker compose up -d --force-recreate api
            </code>
          </Banner>
        </div>
      )}

      <div className="card max-w-2xl">
        <div className="card-head">
          <b className="text-[13px]">Which zone the firm works in</b>
          <span className="flex-1" />
          <span className="mono text-[11px] text-ink-3">running {s.running}</span>
        </div>
        <div className="p-4 space-y-4">
          {/* The common list first. The full set is every zone this server can load,
              which is around six hundred - useful as a fallback and useless as the
              only way to pick the one you already use. */}
          <div>
            <label className="label">Common</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {s.common.map((z) => (
                <button key={z} type="button" onClick={() => setChoice(z)}
                        aria-pressed={want === z}
                        className={`px-2.5 h-8 rounded-md border text-[12.5px] ${
                          want === z
                            ? 'bg-primary text-primary-on border-primary font-medium'
                            : 'bg-surface border-rule text-ink-2 hover:text-ink'}`}>
                  {z}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label" htmlFor="tz-all">Or any other zone</label>
            <select id="tz-all" className="field mt-1" value={want}
                    onChange={(e) => setChoice(e.target.value)}>
              {s.zones.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>

          {/* What a change does and does not do. The second half is the part people
              get wrong: a zone change is forward-looking, and the record of what
              happened keeps the meaning it was written with. */}
          <div className="text-[11.8px] text-ink-3 leading-relaxed space-y-1.5">
            <p>
              <b className="text-ink-2">Takes effect on the next restart.</b> Every
              service holds the clock it was built with, so the zone cannot change
              underneath work already in flight — that is how a task ends up dated in
              one zone with its history in another.
            </p>
            <p>
              <b className="text-ink-2">History is not rewritten.</b> Timestamps are
              stored in UTC, so they read correctly in whichever zone is set. But days
              that have already been closed stay filed under the day they were closed
              on, in the zone that was configured then.
            </p>
          </div>

          <div className="flex gap-2 items-center">
            <button className="btn btn-primary"
                    disabled={!dirty || save.isPending}
                    onClick={() => save.mutate(want)}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            {dirty && (
              <button className="btn" onClick={() => { setChoice(null); setErr(null) }}>
                Cancel
              </button>
            )}
            {dirty && (
              <span className="text-[12px] text-ink-3">
                {want} — a restart will be needed
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
